import { COUNTY_GROUPS } from "@/utils/constants";
import { readJsonPreference } from "@/utils/preferences";

export const DIRECTORY_PAGE_SIZE = 18;
export const MOBILE_DIRECTORY_PAGE_SIZE = 12;

// Phones get a shorter first batch; tablets and up keep the full grid page.
export function getDirectoryPageSize() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return DIRECTORY_PAGE_SIZE;
  return window.matchMedia("(max-width: 639px)").matches ? MOBILE_DIRECTORY_PAGE_SIZE : DIRECTORY_PAGE_SIZE;
}

export function getFavoriteListingIds() {
  const value = readJsonPreference("iwmc_favorites", []);
  return Array.isArray(value) ? value : [];
}

export function radiusFilterActive(filters) {
  return Boolean(filters.nearbyRadius && filters.userLocation);
}

function applyLocationParams(params, filters) {
  if ((filters.sortBy === "nearby" || radiusFilterActive(filters)) && filters.userLocation) {
    params.lat = Number(filters.userLocation.lat.toFixed(3));
    params.lng = Number(filters.userLocation.lng.toFixed(3));
  }
  if (radiusFilterActive(filters)) params.radius = filters.nearbyRadius;
  if (filters.activeZone) params.zone = filters.activeZone;
  else if (filters.activeCounty) params.zones = (COUNTY_GROUPS[filters.activeCounty] || []).join(",");
}

function applyFilterParams(params, filters) {
  if (filters.activeCategory !== "all") params.category = filters.activeCategory;
  if (filters.activeGrade) params.grade = filters.activeGrade;
  if (filters.activeDomain) params.domain = filters.activeDomain;
  if (filters.matchedDomains.length) params.domains = filters.matchedDomains.join(",");
  if (filters.nearbyZip?.cities?.length && !radiusFilterActive(filters)) params.cities = filters.nearbyZip.cities.join(",");
  if (filters.searchQuery) params.q = filters.searchQuery;
  if (filters.detailsFilter !== "all") params.details = filters.detailsFilter;
}

function applyFavoriteParams(params, filters) {
  if (!filters.showFavsOnly) return;
  params.ids = getFavoriteListingIds().join(",");
  params.limit = 500;
}

export function buildListingRequestParams(pageNum, filters) {
  const params = {
    page: pageNum,
    limit: getDirectoryPageSize(),
    sort: filters.sortBy === "nearby" || radiusFilterActive(filters) ? "distance" : filters.sortBy,
  };
  applyLocationParams(params, filters);
  applyFilterParams(params, filters);
  applyFavoriteParams(params, filters);
  return params;
}

export function buildCountyStats(stats) {
  if (!stats?.zones) return [];
  const output = {};
  Object.entries(COUNTY_GROUPS).forEach(([county, zones]) => {
    const totals = zones.reduce((sum, zone) => {
      const current = stats.zones[zone];
      if (!current) return sum;
      return {
        total: sum.total + current.total,
        nutrition: sum.nutrition + current.nutrition,
        fitness: sum.fitness + current.fitness,
        recovery: sum.recovery + current.recovery,
        services: sum.services + (current.services || 0),
        ratingSum: sum.ratingSum + current.ratingSum,
        topGrade: sum.topGrade + current.topGrade,
      };
    }, { total: 0, nutrition: 0, fitness: 0, recovery: 0, services: 0, ratingSum: 0, topGrade: 0 });
    if (totals.total > 0) {
      output[county] = {
        total: totals.total,
        nutrition: totals.nutrition,
        fitness: totals.fitness,
        recovery: totals.recovery,
        services: totals.services,
        avgRating: (totals.ratingSum / totals.total).toFixed(1),
        topGrade: totals.topGrade,
      };
    }
  });
  return Object.entries(output).sort((a, b) => b[1].total - a[1].total);
}

export function buildHeroProofPoints(stats) {
  const proof = stats?.proofPoints;
  if (!proof) return [];
  const points = [];
  if (proof.recentOwnerUpdateCount > 0) {
    points.push({ id: "owner-updates", text: `${proof.recentOwnerUpdateCount.toLocaleString()} owner-confirmed venue ${proof.recentOwnerUpdateCount === 1 ? "record was" : "records were"} updated in the last 30 days.`, href: "/?proof=owner&details=owner#listings-section", action: "View owner-confirmed venues" });
  } else if (proof.ownerVerifiedVenueCount > 0) {
    points.push({ id: "owner-verified", text: `${proof.ownerVerifiedVenueCount.toLocaleString()} venue ${proof.ownerVerifiedVenueCount === 1 ? "record is" : "records are"} owner-confirmed.`, href: "/?proof=owner&details=owner#listings-section", action: "View owner-confirmed venues" });
  }
  if (proof.recentlyReviewedCities?.length) {
    const city = proof.recentlyReviewedCities[0];
    points.push({ id: "reviewed-cities", text: `Recently reviewed city data includes ${proof.recentlyReviewedCities.slice(0, 3).join(", ")}.`, href: `/?proof=city&q=${encodeURIComponent(city)}#listings-section`, action: `View ${city} venues` });
  }
  if (proof.aPlusVenueCount > 0) points.push({ id: "a-plus", text: `${proof.aPlusVenueCount.toLocaleString()} A+ venues are currently indexed in the directory.`, href: "/?proof=grade&grade=A%2B#listings-section", action: "View A+ venues" });
  if (stats.totalListings > 0 && proof.coverageAreaCount > 0) points.push({ id: "coverage", text: `${stats.totalListings.toLocaleString()} directory records span ${proof.coverageAreaCount.toLocaleString()} active coverage areas.`, href: "/areas", action: "Explore coverage areas" });
  return points;
}